import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  onAnimationEnd,
  removeWillChangeOnAnimationEnd,
} from '@theme/utilities';

class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /**
   * Records whether the homepage header was already past the hero
   * before the drawer opened.
   */
  #homepageHeaderWasPastHero = false;

  /**
   * Tracks whether this script temporarily added the
   * homepage-header-past-hero class.
   */
  #forcedHomepageHeaderState = false;

  /**
   * Watches the body class because mobile keyboard and viewport
   * events can cause other header scripts to remove header classes.
   */
  #bodyClassObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onChildMenuClick);
    this.addEventListener('focusin', this.#onFocusIn);

    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onChildMenuClick);
    this.removeEventListener('focusin', this.#onFocusIn);

    this.#stopBodyClassObserver();

    /**
     * Clean up the body state if the drawer component
     * is removed while it is still open.
     */
    if (this.#isRootDrawerOpen()) {
      this.#clearRootDrawerState();
    }
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Keep the correct header state when the mobile search field
   * receives focus. On iOS, opening the keyboard can trigger
   * resize and scroll handlers that change the homepage header.
   */
  #onFocusIn = (event) => {
    if (!(event.target instanceof Element)) return;

    const searchInput = event.target.closest(
      '.menu-drawer__search-input'
    );

    if (!searchInput) return;
    if (!this.#isRootDrawerOpen()) return;

    this.#applyRootDrawerState();
  };

  /**
   * Hide grandchild menu when clicking a child menu link
   * that has href="#" but does not have grandchild items.
   */
  #onChildMenuClick = (event) => {
    const link = event.target.closest(
      '.menu-drawer__menu--childlist a'
    );

    if (!link) return;

    const href = link.getAttribute('href');

    if (!href || !href.trim().endsWith('#')) return;

    const childItem = link.closest(
      '.menu-drawer__list-item, li'
    );

    if (!childItem) return;

    const hasGrandchild =
      childItem.querySelector(
        '.menu-drawer__menu--grandchildlist'
      ) ||
      childItem.querySelector('[data-menu-handle]') ||
      childItem.querySelector('details') ||
      childItem.querySelector('.menu-drawer__submenu');

    document
      .querySelectorAll('.menu-drawer__list-item')
      .forEach((list) => {
        list.classList.remove('is-third-level-open');
      });

    document
      .querySelectorAll('.menu-drawer')
      .forEach((list) => {
        list.classList.remove('is-third-level-active');
      });

    document
      .querySelectorAll(
        '.menu-drawer__menu--grandchildlist, .third-level-menu'
      )
      .forEach((list) => {
        list.classList.remove('is-active');
      });

    if (!hasGrandchild) {
      event.preventDefault();

      const drawer =
        link.closest('.menu-drawer') ||
        this.refs.menuDrawer;

      drawer
        .querySelectorAll(
          '.menu-drawer__menu--grandchildlist'
        )
        .forEach((grandchildList) => {
          grandchildList.setAttribute(
            'aria-hidden',
            'true'
          );

          grandchildList.hidden = true;
          grandchildList.classList.remove('is-active');
        });

      drawer
        .querySelectorAll(
          '.menu-drawer__menu--childlist ' +
            '.menu-drawer__list-item'
        )
        .forEach((item) => {
          item.classList.remove('is-active');
          item.classList.remove('menu-open');
          item.classList.remove('sub-menu-open');
        });

      childItem.classList.add('is-active');
    }
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #isRootDrawerOpen() {
    return (
      this.refs.details.hasAttribute('open') ||
      this.refs.details.classList.contains('menu-open')
    );
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) {
      return this.refs.details;
    }

    return (
      event.target.closest('details') ??
      this.refs.details
    );
  }

  /**
   * Apply the body and homepage header classes required while
   * the main menu drawer is open.
   */
  #applyRootDrawerState() {
    const body = document.body;

    if (!body) return;

    /**
     * Capture the original homepage header state only once
     * for the current drawer session.
     */
    if (!this.#bodyClassObserver) {
      this.#homepageHeaderWasPastHero =
        body.classList.contains(
          'homepage-header-past-hero'
        );

      this.#forcedHomepageHeaderState =
        body.classList.contains('page-index') &&
        !this.#homepageHeaderWasPastHero;
    }

    body.classList.add('menu-drawer-open');

    /**
     * Prevent the homepage transparent-header rule from becoming
     * active while the drawer or mobile search is open.
     */
    if (this.#forcedHomepageHeaderState) {
      body.classList.add(
        'menu-drawer-forced-homepage-header'
      );

      body.classList.add(
        'homepage-header-past-hero'
      );
    }

    this.#startBodyClassObserver();
  }

  /**
   * Remove the temporary drawer and homepage header state.
   */
  #clearRootDrawerState() {
    const body = document.body;

    this.#stopBodyClassObserver();

    if (!body) return;

    body.classList.remove('menu-drawer-open');

    if (this.#forcedHomepageHeaderState) {
      body.classList.remove(
        'menu-drawer-forced-homepage-header'
      );

      body.classList.remove(
        'homepage-header-past-hero'
      );
    }

    this.#homepageHeaderWasPastHero = false;
    this.#forcedHomepageHeaderState = false;
  }

  /**
   * Other homepage scripts may react to mobile keyboard,
   * resize, visual viewport, or scroll events and remove
   * homepage-header-past-hero.
   *
   * Keep the required drawer classes active until the drawer closes.
   */
  #startBodyClassObserver() {
    const body = document.body;

    if (!body || this.#bodyClassObserver) return;

    this.#bodyClassObserver = new MutationObserver(() => {
      if (!this.#isRootDrawerOpen()) return;

      if (!body.classList.contains('menu-drawer-open')) {
        body.classList.add('menu-drawer-open');
      }

      if (
        this.#forcedHomepageHeaderState &&
        !body.classList.contains(
          'homepage-header-past-hero'
        )
      ) {
        body.classList.add(
          'homepage-header-past-hero'
        );
      }

      if (
        this.#forcedHomepageHeaderState &&
        !body.classList.contains(
          'menu-drawer-forced-homepage-header'
        )
      ) {
        body.classList.add(
          'menu-drawer-forced-homepage-header'
        );
      }
    });

    this.#bodyClassObserver.observe(body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  #stopBodyClassObserver() {
    if (!this.#bodyClassObserver) return;

    this.#bodyClassObserver.disconnect();
    this.#bodyClassObserver = null;
  }

  toggle() {
    return this.isOpen
      ? this.close()
      : this.open();
  }

  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    /**
     * Only apply the body and homepage state when opening
     * the main drawer, not when opening a nested submenu.
     */
    if (details === this.refs.details) {
      this.#applyRootDrawerState();
    }

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      /**
       * Reapply after the animation frame because other theme
       * listeners may run after the summary is clicked.
       */
      if (details === this.refs.details) {
        this.#applyRootDrawerState();
      }

      const parentListItem = details.closest(
        '.menu-drawer__list-item'
      );

      const navigation = details.closest(
        '.menu-drawer__navigation'
      );

      if (parentListItem) {
        parentListItem.classList.add(
          'sub-menu-open'
        );
      }

      if (target && navigation) {
        navigation.classList.add(
          'opened-submenu'
        );
      }

      if (target) {
        this.refs.menuDrawer.classList.add(
          'menu-drawer--has-submenu-opened'
        );
      }

      const drawer = details.querySelector(
        '.menu-drawer, .menu-drawer__submenu'
      );

      onAnimationEnd(
        drawer || details,
        () => trapFocus(details),
        {
          subtree: false,
        }
      );
    });
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    /**
     * Clear the body and homepage header classes only when
     * closing the main drawer.
     */
    if (details === this.refs.details) {
      this.#clearRootDrawerState();
    }

    const parentListItem = details.closest(
      '.menu-drawer__list-item'
    );

    const navigation = details.closest(
      '.menu-drawer__navigation'
    );

    if (parentListItem) {
      parentListItem.classList.remove(
        'sub-menu-open'
      );
    }

    if (navigation) {
      navigation.classList.remove(
        'opened-submenu'
      );
    }

    this.refs.menuDrawer.classList.remove(
      'menu-drawer--has-submenu-opened'
    );

    const drawer = details.querySelector(
      '.menu-drawer, .menu-drawer__submenu'
    );

    onAnimationEnd(
      drawer || details,
      () => {
        if (
          summary.getAttribute('aria-expanded') ===
          'true'
        ) {
          return;
        }

        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();

          const openDetails = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );

          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      {
        subtree: false,
      }
    );
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(
      '.menu-drawer__animated-element'
    );

    allAnimated.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      'accordion-custom .details-content'
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add(
          'details-content--no-animation'
        );
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove(
            'details-content--no-animation'
          );
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define(
    'header-drawer',
    HeaderDrawer
  );
}

function reset(element) {
  element.classList.remove('menu-open');
  element.classList.remove('sub-menu-open');

  element
    .closest('.menu-drawer__list-item')
    ?.classList.remove('sub-menu-open');

  element
    .closest('.menu-drawer__navigation')
    ?.classList.remove('opened-submenu');

  element.removeAttribute('open');

  element
    .querySelector('summary')
    ?.setAttribute('aria-expanded', 'false');
}